import { CALCULATOR_DEFAULTS } from '../constants.js';
import { calculateSingleStrain } from '../blend/singleStrain.js';
import {
  decimalsForPrecision,
  percentToMgPerGram,
  thcMgFromGrams,
} from '../units.js';
import { hasValidWheelThc } from './eligibility.js';
import type {
  WheelKeepSizeTradeoff,
  WheelKeepThcTradeoff,
  WheelPotencyDirection,
  WheelPrimaryEvaluationOutcome,
  WheelStrainCandidate,
  WheelTargets,
} from './types.js';

/** Match blend-engine THC compare tolerance for “exact” wheel matches. */
export const WHEEL_THC_MATCH_EPS = 1e-6;

function roundGrams(grams: number, precision: number): number {
  const decimals = decimalsForPrecision(precision);
  const factor = 10 ** decimals;
  return Math.round(grams * factor) / factor;
}

function relationFromSigned(
  signed: number,
  eps: number,
): 'larger' | 'smaller' | 'same' {
  if (Math.abs(signed) <= eps) return 'same';
  return signed > 0 ? 'larger' : 'smaller';
}

function thcRelationFromSigned(
  signed: number,
  eps: number,
): 'more' | 'less' | 'same' {
  if (Math.abs(signed) <= eps) return 'same';
  return signed > 0 ? 'more' : 'less';
}

export function validateWheelTargets(
  targetSizeGrams: number,
  targetThcMg: number,
  scalePrecisionGrams: number = CALCULATOR_DEFAULTS.scalePrecisionGrams,
):
  | { ok: true; targets: WheelTargets }
  | {
      ok: false;
      code: 'invalid_size' | 'invalid_thc' | 'non_finite';
      message: string;
    } {
  if (!Number.isFinite(targetSizeGrams) || !Number.isFinite(targetThcMg)) {
    return {
      ok: false,
      code: 'non_finite',
      message: 'Joint size and THC target must be finite numbers.',
    };
  }
  if (targetSizeGrams <= 0) {
    return {
      ok: false,
      code: 'invalid_size',
      message: 'Joint size must be greater than zero.',
    };
  }
  if (targetThcMg <= 0) {
    return {
      ok: false,
      code: 'invalid_thc',
      message: 'THC target must be greater than zero.',
    };
  }
  if (!Number.isFinite(scalePrecisionGrams) || scalePrecisionGrams <= 0) {
    return {
      ok: false,
      code: 'non_finite',
      message: 'Scale precision must be a positive number.',
    };
  }
  return {
    ok: true,
    targets: {
      targetSizeGrams,
      targetThcMg,
      scalePrecisionGrams,
    },
  };
}

export function buildKeepThcTradeoff(
  thcPercent: number,
  targets: WheelTargets,
): WheelKeepThcTradeoff {
  const exactSizeGrams =
    targets.targetThcMg / percentToMgPerGram(thcPercent);
  const requiredSizeGrams = roundGrams(
    exactSizeGrams,
    targets.scalePrecisionGrams,
  );
  const sizeDifferenceGrams = requiredSizeGrams - targets.targetSizeGrams;
  // Require at least one full scale step; sub-step rounding to 0 g is unusable.
  const usable =
    Number.isFinite(requiredSizeGrams) &&
    requiredSizeGrams + 1e-12 >= targets.scalePrecisionGrams;
  return {
    requestedThcMg: targets.targetThcMg,
    requiredSizeGrams,
    exactSizeGrams,
    sizeDifferenceGrams,
    sizeRelation: relationFromSigned(
      sizeDifferenceGrams,
      targets.scalePrecisionGrams / 2,
    ),
    usable,
    unusableReason: usable
      ? null
      : `The THC target needs about ${exactSizeGrams.toFixed(4)} g, which is below the ${targets.scalePrecisionGrams} g scale step. Raise the THC target or use a less potent strain.`,
  };
}

export function buildKeepSizeTradeoff(
  thcPercent: number,
  targets: WheelTargets,
): WheelKeepSizeTradeoff {
  const resultingThcMg = thcMgFromGrams(
    targets.targetSizeGrams,
    thcPercent,
  );
  const thcDifferenceMg = resultingThcMg - targets.targetThcMg;
  return {
    requestedSizeGrams: targets.targetSizeGrams,
    resultingThcMg,
    thcDifferenceMg,
    thcRelation: thcRelationFromSigned(thcDifferenceMg, WHEEL_THC_MATCH_EPS),
  };
}

export function potencyDirection(
  thcPercent: number,
  requiredAverageThcPercent: number,
): WheelPotencyDirection {
  const diff = thcPercent - requiredAverageThcPercent;
  if (Math.abs(diff) <= WHEEL_THC_MATCH_EPS) return 'exact';
  return diff > 0 ? 'stronger' : 'weaker';
}

export function directionExplanation(
  direction: WheelPotencyDirection,
): string {
  if (direction === 'stronger') {
    return (
      'This strain is stronger than needed for both targets. ' +
      'You can use a smaller joint to keep the THC target, keep the original ' +
      'joint size and accept more THC, or select a balancing strain.'
    );
  }
  if (direction === 'weaker') {
    return (
      'This strain is weaker than needed for both targets. ' +
      'You can use a larger joint to keep the THC target, keep the original ' +
      'joint size and accept less THC, or select a balancing strain.'
    );
  }
  return (
    'This strain matches the average potency needed for both targets ' +
    'within rounding tolerance.'
  );
}

/**
 * Evaluate a spun primary strain against joint size + THC targets.
 * Reuses canonical thcMgFromGrams / calculateSingleStrain.
 */
export function evaluatePrimarySpin(
  strain: WheelStrainCandidate,
  targetSizeGrams: number,
  targetThcMg: number,
  scalePrecisionGrams: number = CALCULATOR_DEFAULTS.scalePrecisionGrams,
): WheelPrimaryEvaluationOutcome {
  const validated = validateWheelTargets(
    targetSizeGrams,
    targetThcMg,
    scalePrecisionGrams,
  );
  if (!validated.ok) return validated;

  if (!hasValidWheelThc(strain.thcPercent)) {
    if (
      strain.thcPercent === null ||
      strain.thcPercent === undefined ||
      Number.isNaN(strain.thcPercent as number)
    ) {
      return {
        ok: false,
        code: 'missing_thc',
        message:
          'This strain has no usable THC percentage, so it cannot be evaluated.',
      };
    }
    if (strain.thcPercent === 0) {
      return {
        ok: false,
        code: 'zero_thc',
        message: 'This strain reports 0% THC and cannot be used on the wheel.',
      };
    }
    return {
      ok: false,
      code: 'invalid_strain_thc',
      message: 'This strain’s THC percentage is invalid and cannot be used.',
    };
  }

  const thcPercent = strain.thcPercent;
  const { targets } = validated;
  const requiredAverageThcPercent =
    targets.targetThcMg / (targets.targetSizeGrams * 10);

  const keepSize = buildKeepSizeTradeoff(thcPercent, targets);
  const keepThc = buildKeepThcTradeoff(thcPercent, targets);

  const single = calculateSingleStrain({
    strainName: strain.name ?? strain.productName ?? 'Selected strain',
    thcPercent,
    targetThcMg: targets.targetThcMg,
    scalePrecisionGrams: targets.scalePrecisionGrams,
    mode: 'closest',
  });

  const thcAtTargetSize = keepSize.resultingThcMg;
  const sizeMatchesTarget =
    Math.abs(thcAtTargetSize - targets.targetThcMg) <= WHEEL_THC_MATCH_EPS;
  const weightMatchesTarget =
    single.ok &&
    Math.abs(single.recommendedWeightGrams - targets.targetSizeGrams) <=
      targets.scalePrecisionGrams / 2 + 1e-12;

  if (sizeMatchesTarget || weightMatchesTarget) {
    const strainWeightGrams = sizeMatchesTarget
      ? roundGrams(targets.targetSizeGrams, targets.scalePrecisionGrams)
      : single.ok
        ? single.recommendedWeightGrams
        : roundGrams(targets.targetSizeGrams, targets.scalePrecisionGrams);
    const estimatedThcMg = thcMgFromGrams(strainWeightGrams, thcPercent);
    return {
      ok: true,
      strain,
      targets,
      thcPercent,
      requiredAverageThcPercent,
      evaluation: {
        kind: 'exact_match',
        strainWeightGrams,
        totalWeightGrams: strainWeightGrams,
        estimatedThcMg,
        roundingDifferenceMg: estimatedThcMg - targets.targetThcMg,
      },
    };
  }

  const direction = potencyDirection(thcPercent, requiredAverageThcPercent);
  return {
    ok: true,
    strain,
    targets,
    thcPercent,
    requiredAverageThcPercent,
    evaluation: {
      kind: 'tradeoffs',
      direction,
      keepThc,
      keepSize,
      explanation: directionExplanation(direction),
    },
  };
}
